import { useEffect } from "react";

import PaginationFooter from "common/components/PaginationFooter";
import usePagination from "common/hooks/usePagination";

import MobileSelectionList, {
  type MobileSelectionListProps,
} from "./MobileSelectionList";

type PaginatedMobileSelectionListProps<
  DataType extends { id: string },
  ItemKey extends string,
> = Omit<MobileSelectionListProps<DataType, ItemKey>, "totalDataCount"> & {
  localStorageId: string;
  isLoading?: boolean;
};

export default function PaginatedMobileSelectionList<
  DataType extends { id: string },
  ItemKey extends string,
>({
  title,
  data,
  localStorageId,
  isLoading = false,
  selection,
  onSelectionChange,
  allItems,
  children,
}: PaginatedMobileSelectionListProps<DataType, ItemKey>) {
  const { page, setPage, pageSize, setPageSize } =
    usePagination(localStorageId);

  const maxPage = Math.max(0, Math.ceil(data.length / pageSize) - 1);
  const currentPage = Math.min(page, maxPage);

  useEffect(() => {
    setPage(0);
  }, [data]);

  useEffect(() => {
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, maxPage]);

  const pageItems = isLoading
    ? (new Array(pageSize).fill({}) as DataType[])
    : data.slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  return (
    <>
      <MobileSelectionList
        title={title}
        data={pageItems}
        selection={selection}
        onSelectionChange={onSelectionChange}
        allItems={allItems}
        totalDataCount={data.length}
      >
        {children}
      </MobileSelectionList>

      <PaginationFooter
        pageSize={pageSize}
        setPageSize={setPageSize}
        selectedPage={currentPage}
        setSelectedPage={setPage}
        totalItemCount={data.length}
        isLastPageHidden={false}
      />
    </>
  );
}
